import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { roundToStep } from 'assets/utils/data/Number';
import { exportListToExcel } from 'assets/utils/file/Excel';
import { HttpStatus, defaultLimit, defaultOffset } from 'config/Api.Config';
import { sumBy, uniq, xorBy } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import PaymentType from 'models/enums/PaymentType';
import contractRefundPaymentSaveApi from 'models/finance/contractRefundPayment/save/Api.ContractRefundPayment.Save';
import deleteContractRefundPaymentItemsByContractRefundId from 'models/finance/contractRefundPaymentItem/delete/Api.ContractRefundPaymentItem.Delete';
import contractRefundPaymentItemExportApi from 'models/finance/contractRefundPaymentItem/export/Api.ContractRefundPaymentItem.Export';
import contractRefundPaymentItemListApi from 'models/finance/contractRefundPaymentItem/list/Api.ContractRefundPaymentItem.List';
import ContractRefundPaymentItem from 'models/finance/contractRefundPaymentItem/Model.ContractRefundPaymentItem';
import PostingAccount from 'models/finance/postingAccount/Model.PostingAccount';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import contractRefundSetStatusPayedApi from 'models/sales/contractRefund/setStatusPayed/Api.ContractRefund.SetStatusPayed';
import { useMemo, useState } from 'react';
import { financeContractRefundPaymentItemIndexRoute } from './Finance.ContractRefundPaymentItem.Index';

export enum ContractRefundPaymentItemAction {
  ADD = 'add',
  EDIT = 'edit',
  INFO = 'info',
  CREATE_PAYMENT = 'createPayment',
  EXCLUDE = 'exclude',
}

export type VerifyContractRefundPaymentItemAction = Utils.VerifyExtends<
  Module.Finance.ContractRefundPaymentItem.Actions,
  Utils.ValueOf<typeof ContractRefundPaymentItemAction>
>;

export function useContractRefundPaymentItemRouter() {
  return usePageRouter<Module.Finance.ContractRefundPaymentItem.Params, Module.Finance.ContractRefundPaymentItem.Query>(
    {
      route: financeContractRefundPaymentItemIndexRoute,
    }
  );
}
export default function useContractRefundPaymentItem() {
  const { lang } = getLocales();
  const pageRouter = useContractRefundPaymentItemRouter();

  const { permissions, user } = useAuthStore();

  const [selectedItems, setSelectedItems] = useState<ContractRefundPaymentItem[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const canView = permissions.VIEW_CONTRACTREFUNDPAYMENTITEM || permissions.CREATE_CONTRACTREFUNDPAYMENTITEM;

  //#region Api calls
  const listApi = useApi(
    {
      action: ContractRefundPaymentItem.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        hasContractRefundPayment: false,
        isCancelled: false,
        isExcluded: false,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const findApi = useApi(
    {
      action: ContractRefundPaymentItem.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );

  function reload() {
    listApi.execute((b) => b);
    findApi.execute((b) => b);
  }
  //#endregion

  const userApi = useApi({ action: User.list, body: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    body: {
      id: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: true,
  });

  const [paymentCount, setPaymentCount] = useState<number>(listApi.payload?.data?.length);
  async function getAllSelectedContractRefundPaymentItems(): Promise<Model.IContractRefundPaymentItem[]> {
    const newQuery = { ...pageRouter?.query?.list };
    delete newQuery.limit;
    delete newQuery.offset;

    const {
      payload: { data: filteredItems },
    } = await contractRefundPaymentItemListApi({ ...newQuery });
    const selectableItems = filteredItems?.filter((it) => !it.isCancelled && !it.isExcluded);

    return selectedItems?.length === selectableItems?.length
      ? []
      : (selectableItems as Model.IContractRefundPaymentItem[]);
  }

  const toggleItem = (contractRefundPaymentItem: ContractRefundPaymentItem) => {
    const newSelectedItemList = xorBy(selectedItems || [], [contractRefundPaymentItem], 'id');
    setSelectedItems(newSelectedItemList);
  };

  const toggleSelectAll = async (selectAll: boolean) => {
    if (selectAll) {
      const fetchedItems = await getAllSelectedContractRefundPaymentItems();
      setPaymentCount(fetchedItems.length);
      setSelectedItems(fetchedItems.map((it) => new ContractRefundPaymentItem(it)));
    } else {
      setSelectedItems([]);
      setPaymentCount(listApi.payload?.data?.length);
    }
  };

  const disableCreatePayment =
    !permissions.CREATE_CONTRACTREFUNDPAYMENT ||
    selectedItems?.length === 0 ||
    uniq(selectedItems.map((item) => item.contractRefundDealerPaymentControl?.contractRefundId)).length > 1 ||
    uniq(selectedItems.map((item) => item.contractRefundPayeeId)).length > 1 ||
    uniq(selectedItems.map((item) => item.currency)).length > 1 ||
    selectedItems.some((it) => it.isCancelled || it.isExcluded || it.contractRefundPaymentId);

  async function excludeItem(contractRefundPaymentItem: ContractRefundPaymentItem) {
    const response = await ContractRefundPaymentItem.save({ ...contractRefundPaymentItem.toJSON(), isExcluded: true });
    if (response.status === HttpStatus.ok) {
      setSelectedItems(selectedItems.filter((it) => it.id !== contractRefundPaymentItem.id));
      pageRouter.updateParams({});
      listApi.execute((b) => b);
      addLog({ success: lang.saveSuccess });
    } else {
      addLog({ error: response.message || lang.error });
    }
  }

  const payment = useMemo(() => {
    const contractRefundId = selectedItems[0]?.contractRefundDealerPaymentControl?.contractRefundId;

    const currency = selectedItems[0]?.currency;
    const payee = selectedItems[0]?.contractRefundPayee;
    const paymentMethodCode = selectedItems[0]?.paymentMethodCode;
    const amountPaid = roundToStep(
      sumBy(selectedItems || [], (item) => Number(item.totalAmountBeforeTax) || 0),
      0.01
    );
    const gstHstAmountPaid = roundToStep(
      sumBy(selectedItems || [], (item) => Number(item.gstHstAmount) || 0),
      0.01
    );
    const provincialSalesTaxAmountPaid = roundToStep(
      sumBy(selectedItems || [], (item) => Number(item.provincialSalesTaxAmount) || 0),
      0.01
    );

    return {
      contractRefundId,
      currency,
      payee,
      contractRefundPaymentPayeeId: payee?.id,
      payeeEmail: payee?.paymentEmail,
      paymentMethodCode,
      isETransferOrUsBank: paymentMethodCode === PaymentType.usBank || paymentMethodCode === PaymentType.ETransfer,
      amountPaid,
      gstHstAmountPaid,
      provincialSalesTaxAmountPaid,
    };
  }, [selectedItems]);

  const contractRefundApi = useApi(
    {
      action: ContractRefund.find,
      body: { id: payment?.contractRefundId },
      wait: !payment?.contractRefundId,
    },
    [payment?.contractRefundId]
  );
  const contractRefund =
    contractRefundApi?.payload ?? selectedItems[0]?.contractRefundDealerPaymentControl?.contractRefund;

  const postingAccountListApi = useApi(
    {
      action: PostingAccount.list,
      default: {
        isActive: true,
        isPayable: true,
        currency: selectedItems[0]?.currency,
        productId: contractRefund?.contractCancellation?.contract?.productId
          ? [contractRefund?.contractCancellation?.contract?.productId]
          : undefined,
      },
      wait: !contractRefund?.contractCancellation?.contract?.productId || !selectedItems[0]?.currency,
    },
    [contractRefund?.contractCancellation?.contract?.productId, selectedItems[0]?.currency]
  );

  const createContractRefundPayment = async (data: Api.Finance.ContractRefundPayment.Save.IRequest) => {
    if (!selectedItems?.length) {
      addLog({ error: lang.mustSelectAtleastOneOption });
      return;
    }
    if (selectedItems.some((it) => it.isCancelled || it.isExcluded || it.contractRefundPaymentId)) {
      addLog({ error: lang.error });
      return;
    }

    const response = await contractRefundPaymentSaveApi({
      ...data,
      currency: payment?.currency,
      contractRefundPaymentItems: selectedItems.map((it) => ({ id: it.id })),
    });

    if (response.status === HttpStatus.ok) {
      await contractRefundSetStatusPayedApi({ id: payment?.contractRefundId });

      setSelectedItems([]);
      listApi.execute((b) => b);
      addLog({ success: response.message || lang.saveSuccess });
      pageRouter.updateParams({});
    } else {
      addLog({ error: response.message || lang.error });
    }
  };

  async function deleteItemsForRefund(contractRefundId: number) {
    const response = await deleteContractRefundPaymentItemsByContractRefundId({ contractRefundId });
    if (response.status === HttpStatus.ok) {
      addLog({ success: lang.saveSuccess });
      pageRouter.updateParams({});
      listApi.execute((b) => b);
    } else {
      addLog({ error: response.message || lang.error });
    }
  }

  async function exportToExcel() {
    setIsExporting(true);
    try {
      await exportListToExcel(contractRefundPaymentItemExportApi, pageRouter.query.list, lang.contractsPaymentPending);
    } finally {
      setIsExporting(false);
    }
  }

  return {
    listApi,
    userApi,
    dealersApi,
    pageRouter,
    permissions,
    selected: pageRouter.params.id ? findApi?.payload : null,
    canView,
    toggleItem,
    toggleSelectAll,
    selectedItems,
    createContractRefundPayment,
    disableCreatePayment,
    excludeItem,
    deleteItemsForRefund,
    reload,
    payment,
    exportToExcel,
    isExporting,
    getAllSelectedContractRefundPaymentItems,
    paymentCount,
    contractRefund,
    postingAccountListApi,
    views: {
      [ContractRefundPaymentItemAction.CREATE_PAYMENT]:
        pageRouter.params.action === ContractRefundPaymentItemAction.CREATE_PAYMENT &&
        permissions.CREATE_CONTRACTREFUNDPAYMENT,
      [ContractRefundPaymentItemAction.EXCLUDE]:
        pageRouter.params.action === ContractRefundPaymentItemAction.EXCLUDE &&
        permissions.EDIT_CONTRACTREFUNDPAYMENTITEM,
    },
  };
}

export function useContractRefundPaymentItemProvider() {
  return useDataProvider<ReturnType<typeof useContractRefundPaymentItem>>();
}
